import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** One explicit scent preview at a time, shared by all product cards. */
let activeScentGallery;
let scentDismissController;

export function hydrateScentAccords(scope) {
  if (!(scope instanceof Element)) return;
  let media = scope.matches('.product-media[data-oleum-scent]') ? scope : null;
  if (!media) {
    const slideshow = scope.querySelector('slideshow-component');
    const current = slideshow?.slides?.[slideshow.current];
    const slide = current || scope.querySelector('slideshow-slide:not([hidden])');
    media = (slide || scope).querySelector('.product-media[data-oleum-scent]');
  }
  if (!media) return;
  const existing = media.querySelector(':scope > svg.oleum-accords');
  if (existing) return existing;
  const source = media.querySelector(':scope > script[type="application/x-oleum-scent-accords"]');
  if (!source) return;
  const template = document.createElement('template');
  template.innerHTML = source.textContent || '';
  const chart = template.content.querySelector('svg.oleum-accords');
  if (chart) source.after(chart);
  return chart;
}

/** @param {HTMLElement} gallery @param {boolean} open */
export function setScentAccords(gallery, open) {
  if (open) hydrateScentAccords(gallery);
  if (open && activeScentGallery && activeScentGallery !== gallery) {
    setScentAccords(activeScentGallery, false);
  }
  gallery.toggleAttribute('data-oleum-accords-open', open);
  gallery.toggleAttribute('data-mobile-preview-open', open);
  const button = gallery.querySelector('[data-oleum-accords-toggle]');
  if (button instanceof HTMLElement) {
    button.setAttribute('aria-pressed', String(open));
    button.setAttribute('aria-label', (open ? button.dataset.hideLabel : button.dataset.showLabel) || '');
  }
  if (!open) {
    if (activeScentGallery === gallery) {
      activeScentGallery = undefined;
      scentDismissController?.abort();
      scentDismissController = undefined;
    }
    return;
  }
  activeScentGallery = gallery;
  scentDismissController?.abort();
  scentDismissController = new AbortController();
  const { signal } = scentDismissController;
  const dismissOutside = (event) => {
    if (!event.composedPath().includes(gallery)) setScentAccords(gallery, false);
  };
  document.addEventListener('pointerdown', dismissOutside, { capture: true, passive: true, signal });
  document.addEventListener('click', dismissOutside, { capture: true, signal });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setScentAccords(gallery, false);
  }, { signal });
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('pointerover', this.#hydrateAccords, { passive: true, signal });
    this.addEventListener('focusin', this.#hydrateAccords, { signal });
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.querySelectorAll('.product-media-container:hover .product-media[data-oleum-scent], .product-media-container:focus-within .product-media[data-oleum-scent]').forEach(hydrateScentAccords);
    }
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #hydrateAccords = (event) => {
    if (!(event.target instanceof Element) || event.pointerType === 'touch') return;
    const media = event.target.closest('.product-media[data-oleum-scent]') ||
      event.target.closest('.product-media-container')?.querySelector('.product-media[data-oleum-scent]');
    if (media && this.contains(media)) hydrateScentAccords(media);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
